import type { HotelPlaceSuggestion, HotelSearchValue } from '../models.js';

/** Um campo da barra que ainda não foi respondido. */
export type HotelSearchPendingField = 'places' | 'dates' | 'rooms';

/**
 * O que falta responder na barra, NA ORDEM em que a fileira mostra (QUEM ·
 * ONDE · QUANDO, a mesma do ônibus) — então o primeiro item é sempre "o
 * próximo campo pendente".
 *
 * Hóspedes: o campo nasce com ocupação (`[{ guests: 1 }]`), nunca está vazio
 * — mas ocupação não é o mesmo que hóspede NOMEADO. Uma vaga sem pessoa
 * atribuída (`guests` maior que `travelerIds.length`) é pergunta sem
 * resposta: o subtítulo não pode dizer "tudo pronto" e o painel "Quem" é o
 * próximo passo natural — por isso o id `'rooms'` casa com o id do painel da
 * barra.
 *
 * O destino é o TEXTO, e só ele. Duas metades desse critério:
 *
 * - Texto sem `placeId` conta como respondido: o agente pré-preenche a cidade
 *   sem resolver o lugar, e abrir o popover por cima de um destino já escrito
 *   seria corrigir o que ninguém errou.
 * - `placeId` sem texto NÃO conta. `placeDetails` traz `name`/`city`
 *   opcionais, então um bloco com coordenada e sem nome rende `place: ''` com
 *   `selectedPlaceId` preenchido — e olhar para o id ali daria o campo por
 *   respondido enquanto o viajante vê uma caixa vazia, sem o painel que
 *   deveria ajudá-lo. É o único estado em que as duas condições divergem, e
 *   nele o id está errado: quem responde a pergunta "para onde?" na tela é o
 *   texto.
 */
export function pendingHotelSearchFields(
  value: HotelSearchValue,
): readonly HotelSearchPendingField[] {
  const pending: HotelSearchPendingField[] = [];
  const guests = value.rooms.reduce((sum, room) => sum + room.guests, 0);
  if (guests > value.travelerIds.length) pending.push('rooms');
  if (!value.place.trim()) pending.push('places');
  if (!value.checkin || !value.checkout) pending.push('dates');
  return pending;
}

/**
 * Quantos hóspedes o resumo do Quem anuncia: os NOMEADOS, não as vagas. Uma
 * vaga vazia contava como hóspede ("3 hóspedes · 2 quartos" com um quarto sem
 * ninguém — #3217 C8), e o número não batia com o que a pessoa via nos campos.
 * Piso 1 pelo mesmo motivo do piso dos quartos: o campo nunca anuncia "0".
 */
export function namedGuestCount(value: Pick<HotelSearchValue, 'travelerIds'>): number {
  return Math.max(1, value.travelerIds.length);
}

/** O valor depois de um pick do ONDE. A origem do lugar (`placeSource`) sai:
 *  quem escolheu na lista foi a pessoa, mesmo que seja o lugar que a posição
 *  deu. */
function withPlace(value: HotelSearchValue, place: HotelPlaceSuggestion): HotelSearchValue {
  return { ...value, place: place.label, selectedPlaceId: place.id, placeSource: undefined };
}

/** Um pick do ONDE, igual no popover e na folha: avisa o host (`onPlacePick`)
 *  antes de gravar, para o pedido de posição em voo cair antes do valor novo, e
 *  limpa a busca. Devolve o valor gravado. */
export function commitPlace(
  bar: {
    readonly value: HotelSearchValue;
    onChange(value: HotelSearchValue): void;
    onPlaceQueryChange(query: string): void;
    onPlacePick?(): void;
  },
  place: HotelPlaceSuggestion,
  setPlaceQuery: (query: string | null) => void,
): HotelSearchValue {
  const next = withPlace(bar.value, place);
  bar.onPlacePick?.();
  bar.onChange(next);
  setPlaceQuery(null);
  bar.onPlaceQueryChange('');
  return next;
}
